import { createActionsHook, createHook, createStore, type StoreActionApi } from 'react-sweet-state';

import { ConversationAssistantUIModes } from './types';

/**
 * Interface for controlling the conversation assistant sidebar panel state.
 * Consumers (like Confluence) can provide their own implementation
 * to integrate with their sidebar management system. Currently the
 * same interface as AISmartButtonSidebarAPI in @atlassian/ai-smart-button.
 */
export type RovoExperienceSidebarAPI = {
	isSidebarOpen: () => boolean;
	openSidebar: () => void;
	closeSidebar: () => void;
};

export type RovoExperienceAPIState = {
	mode?: ConversationAssistantUIModes;
	isMiniModalAvailable?: boolean;
};

const initialState: RovoExperienceAPIState = {
	mode: undefined,
	isMiniModalAvailable: false,
};

const store = createStore({
	name: 'rovoExperienceAPIStore',
	initialState,
	actions: {
		// created for resetting store during tests
		reset:
			() =>
			({ setState }: StoreActionApi<RovoExperienceAPIState>) => {
				setState(initialState);
			},
		setMode:
			(mode: ConversationAssistantUIModes) =>
			({ setState }: StoreActionApi<RovoExperienceAPIState>) => {
				setState({ mode });
			},
		toggleConversationAssistantUIMode:
			({
				targetMode,
				sidebarApi,
			}: {
				targetMode?: ConversationAssistantUIModes;
				sidebarApi: RovoExperienceSidebarAPI;
			}) =>
			({ getState, setState }: StoreActionApi<RovoExperienceAPIState>) => {
				const { mode: currentMode } = getState();

				if (!targetMode) {
					setState({ mode: undefined });

					if (currentMode === ConversationAssistantUIModes.SIDEBAR) {
						sidebarApi.closeSidebar();
					}

					return;
				}

				// No current mode - open target mode
				if (!currentMode) {
					setState({ mode: targetMode });
					if (targetMode === ConversationAssistantUIModes.SIDEBAR) {
						sidebarApi.openSidebar();
					}
					return;
				}
				// Same mode - close everything
				if (currentMode === targetMode) {
					if (targetMode === ConversationAssistantUIModes.SIDEBAR) {
						// If the store thinks we're in SIDEBAR but it's not actually open, open it
						if (!sidebarApi.isSidebarOpen()) {
							sidebarApi.openSidebar();
							setState({ mode: targetMode });
							return;
						}

						// Otherwise, close sidebar and clear mode
						sidebarApi.closeSidebar();
						setState({ mode: undefined });
						return;
					}

					setState({ mode: undefined });
					return;
				}
				// Different mode - handle transition
				switch (targetMode) {
					case ConversationAssistantUIModes.SIDEBAR:
						// Clear mode first to unmount mini-modal
						setState({ mode: undefined });
						// Wait for unmount before opening sidebar
						Promise.resolve().then(() => {
							sidebarApi.openSidebar();
							setState({ mode: targetMode });
						});
						break;

					case ConversationAssistantUIModes.MINI_MODAL:
						if (sidebarApi.isSidebarOpen()) {
							sidebarApi.closeSidebar();
						}
						setState({ mode: targetMode });
						break;
				}
			},
		setIsMiniModalAvailable:
			(isMiniModalAvailable: boolean) =>
			({ setState }: StoreActionApi<RovoExperienceAPIState>) => {
				setState({ isMiniModalAvailable });
			},
	},
});

const useStore = createHook(store);

export const useRovoExperienceAPIStore = () => {
	const [
		{ mode, isMiniModalAvailable },
		{ reset, toggleConversationAssistantUIMode, setIsMiniModalAvailable },
	] = useStore();

	return {
		mode,
		reset,
		toggleConversationAssistantUIMode,
		setIsMiniModalAvailable,
		isMiniModalAvailable,
	};
};

export const useRovoExperienceAPIStoreActions = createActionsHook(store);
